import { uniq, uniqWith } from 'es-toolkit';
import React from 'react';
import { Box, Button, Divider } from '@mui/material';

import { INDETERMINATE, isIndeterminate } from '@paragon/client-tools';
import { CeilingPlane, RoofPlane, RoofPlaneAbsoluteGeometry } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import {
  defaultTopChordSlopeInDegreesForHeelHeightCalculations,
  EditedPlaneProperties,
  heelSettingsAreExactlyEqual,
} from '@paragon/layout';
import { PropertiesPanel } from '@paragon/layout-models';
import { AngleField, DistanceField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import { editCeilingPlanes } from '../../../../store/actions/layout/ceilingPlaneActions';
import { editRoofPlanes, launchFasciaHeightsTool } from '../../../../store/actions/layout/roofPlaneActions';
import { getAbsolutePlaneElevationFromActiveLevel } from '../../../../store/selectors/layoutDataSelectors';
import { isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PrimaryHeelSettingsMethodInputs } from './HeelSettings';
import { ProjectHierarchyContextSelects } from './ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { AutoCutPlanesPropertiesPanelButton } from './roof-planes/AutoCutPlanesButtons';
import { SyncElevationButton } from './SyncElevationButton';

interface BulkPlanePropertiesForPlaneType<Type, Plane> {
  readonly type: Type;
  readonly planes: readonly Plane[];
}

/*
  Note that we strongly desire to keep this panel as visually similar to the singular roof plane properties panel as
    possible, as well as both panels for ceiling planes. Please consider updating all these panels when making any
    changes here.
 */

export function BulkPlaneProperties(
  props: BulkPlanePropertiesForPlaneType<'roof', RoofPlane> | BulkPlanePropertiesForPlaneType<'ceiling', CeilingPlane>,
) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const absolutePlaneElevationFromLevel = useAppSelector(getAbsolutePlaneElevationFromActiveLevel);

  const pluralTerm = props.type === 'roof' ? 'Roof Planes' : 'Ceiling Planes';

  const levelGuids = uniq(props.planes.map((plane) => plane.levelGuid));
  const commonLevelGuid = levelGuids.length === 1 ? levelGuids[0] : INDETERMINATE;

  const slopes = uniq(props.planes.map((plane) => plane.slopeInDegrees));

  const commonSlope = slopes.length === 1 ? slopes[0] : INDETERMINATE;

  const heelSettingses =
    props.type === 'roof'
      ? uniqWith(
          props.planes.map((plane) => plane.heelSettings),
          heelSettingsAreExactlyEqual,
        )
      : null;
  const commonHeelSettings = heelSettingses ? (heelSettingses.length === 1 ? heelSettingses[0] : INDETERMINATE) : null;

  const topChordWidths = props.type === 'roof' ? uniq(props.planes.map((plane) => plane.topChordWidth)) : null;
  const commonTopChordWidth = topChordWidths ? (topChordWidths.length === 1 ? topChordWidths[0] : INDETERMINATE) : null;

  const overhangs = props.type === 'roof' ? uniq(props.planes.map((plane) => plane.overhang)) : null;
  const commonOverhang = overhangs ? (overhangs.length === 1 ? overhangs[0] : INDETERMINATE) : null;

  const subfasciaThicknesses =
    props.type === 'roof' ? uniq(props.planes.map((plane) => plane.subfasciaThickness)) : null;
  const commonSubfasciaThickness = subfasciaThicknesses
    ? subfasciaThicknesses.length === 1
      ? subfasciaThicknesses[0]
      : INDETERMINATE
    : null;

  const cantilevers = props.type === 'roof' ? uniq(props.planes.map((plane) => plane.cantilever)) : null;
  const commonCantilever = cantilevers ? (cantilevers.length === 1 ? cantilevers[0] : INDETERMINATE) : null;

  const deckingThicknesses = props.type === 'roof' ? uniq(props.planes.map((plane) => plane.deckingThickness)) : null;
  const commonDeckingThickness = deckingThicknesses
    ? deckingThicknesses.length === 1
      ? deckingThicknesses[0]
      : INDETERMINATE
    : null;

  const absoluteGeometries = props.planes
    .map((plane) => plane.geometry)
    .filter((g): g is RoofPlaneAbsoluteGeometry => g.type === 'Absolute');

  let commonElevation = null;
  if (absoluteGeometries.length === props.planes.length) {
    const elevations = uniq(absoluteGeometries.map((geometry) => geometry.elevation));
    commonElevation = elevations.length === 1 ? elevations[0] : INDETERMINATE;
  }

  async function editPlanes(editedPlanes: { guid: string; editedProperties: EditedPlaneProperties }[]) {
    if (props.type === 'roof') {
      await dispatch(editRoofPlanes(editedPlanes));
    } else if (props.type === 'ceiling') {
      await dispatch(editCeilingPlanes(editedPlanes));
    } else {
      assertExhaustiveSwitch(props);
    }
  }

  async function editSelectedPlanes(editedProperties: EditedPlaneProperties) {
    const editedPlanes = props.planes.map((plane) => ({
      guid: plane.guid,
      editedProperties,
    }));

    editPlanes(editedPlanes);
  }

  return (
    <PropertiesPanel
      title={`Multiple ${pluralTerm}`}
      titleButton={
        <PropertiesPanelMenuButton
          type={props.type === 'roof' ? 'roofPlane' : 'ceilingPlane'}
          guid={props.planes[0].guid}
        />
      }
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 1, marginTop: '8px!important' }}>
        <TransformationButtons sx={{ gridColumn: '1 / 3', marginTop: -1, marginBottom: 1 }} />

        <Divider sx={{ gridColumn: '1 / 3', marginTop: 1 }} />

        {!isIndeterminate(commonLevelGuid) && (
          <Box sx={{ gridColumn: '1 / 3', marginTop: 2 }}>
            <ProjectHierarchyContextSelects
              levelGuid={commonLevelGuid}
              onChange={async (levelGuid) => await editSelectedPlanes({ levelGuid })}
              disabled={viewOnly}
            />
          </Box>
        )}

        <AngleField
          label="Slope"
          defaultDisplayFormat="slope"
          canBeIndeterminate
          value={commonSlope}
          onSubmit={async (slopeInDegrees) => {
            await editSelectedPlanes({ slopeInDegrees });
          }}
          disabled={viewOnly}
        />

        {commonHeelSettings != null && commonTopChordWidth != null && (
          <>
            <PrimaryHeelSettingsMethodInputs
              heelSettings={commonHeelSettings}
              topChordWidth={isIndeterminate(commonTopChordWidth) ? 3.5 : (commonTopChordWidth as number)}
              topChordSlopeInDegrees={
                typeof commonSlope === 'number' ? commonSlope : defaultTopChordSlopeInDegreesForHeelHeightCalculations
              }
              submit={async (heelSettings) => await editSelectedPlanes({ heelSettings })}
              disabled={viewOnly}
            />
            <DistanceField
              label="Top Chord Width"
              canBeIndeterminate
              value={commonTopChordWidth}
              min={0}
              max={12}
              onSubmit={async (topChordWidth) => await editSelectedPlanes({ topChordWidth })}
              disabled={viewOnly}
            />
          </>
        )}

        {commonOverhang != null && (
          <DistanceField
            label="Overhang"
            canBeIndeterminate
            value={commonOverhang}
            onSubmit={async (overhang) => {
              await editSelectedPlanes({ overhang });
            }}
            disabled={viewOnly}
          />
        )}

        {commonSubfasciaThickness != null && (
          <DistanceField
            label="Subfascia Thickness"
            canBeIndeterminate
            value={commonSubfasciaThickness}
            onSubmit={async (subfasciaThickness) => {
              await editSelectedPlanes({ subfasciaThickness });
            }}
            disabled={viewOnly}
          />
        )}

        {commonCantilever != null && (
          <DistanceField
            label="Cantilever"
            canBeIndeterminate
            value={commonCantilever}
            onSubmit={async (cantilever) => await editSelectedPlanes({ cantilever })}
            disabled={viewOnly}
          />
        )}

        {commonDeckingThickness != null && (
          <DistanceField
            label="Decking Thickness"
            min={0}
            canBeIndeterminate
            value={commonDeckingThickness}
            onSubmit={async (deckingThickness) => await editSelectedPlanes({ deckingThickness })}
            disabled={viewOnly}
          />
        )}

        {commonElevation !== null && (
          <>
            <DistanceField
              label="Elevation"
              canBeIndeterminate
              value={commonElevation}
              onSubmit={async (elevation) => {
                await editPlanes(
                  props.planes.map((plane) => {
                    if (plane.geometry.type !== 'Absolute') {
                      throw new Error('Attempted to edit elevation of non-absolute plane.');
                    }

                    return {
                      guid: plane.guid,
                      editedProperties: { geometry: { ...plane.geometry, elevation } },
                    };
                  }),
                );
              }}
              disabled={viewOnly}
            />

            <SyncElevationButton
              sx={{ marginTop: 1, gridColumn: 'span 2' }}
              disabled={viewOnly || absolutePlaneElevationFromLevel === null}
              visible={commonLevelGuid != null}
              synchronized={
                absolutePlaneElevationFromLevel !== null &&
                !isIndeterminate(commonElevation) &&
                distanceEquals(commonElevation, absolutePlaneElevationFromLevel)
              }
              onSynchronize={async () => {
                if (absolutePlaneElevationFromLevel === null)
                  throw new Error('sync elevation button for absolute planes clicked when it was disabled.');

                await editPlanes(
                  props.planes.map((plane) => {
                    if (plane.geometry.type !== 'Absolute') {
                      throw new Error('Attempted to edit elevation of non-absolute plane.');
                    }

                    return {
                      guid: plane.guid,
                      editedProperties: {
                        geometry: { ...plane.geometry, elevation: absolutePlaneElevationFromLevel },
                      },
                    };
                  }),
                );
              }}
            />

            <Divider sx={{ gridColumn: '1 / 3', marginTop: 2 }} />
          </>
        )}

        {props.type === 'roof' && (
          <Button
            sx={{ marginTop: 2, marginBottom: 2, gridColumn: 'span 2' }}
            variant="outlined"
            disabled={viewOnly}
            onClick={() => {
              dispatch(launchFasciaHeightsTool());
            }}
          >
            Adjust Fascia Height
          </Button>
        )}
      </Box>

      {props.type === 'roof' && (
        <AutoCutPlanesPropertiesPanelButton roofPlaneGuids={props.planes.map(({ guid }) => guid)} disabled={viewOnly} />
      )}
    </PropertiesPanel>
  );
}
